/*!
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */

import { createFeatureSelector, createSelector } from '@ngrx/store';

import {
  EDeviceErrorType,
  EPlotType,
  EStatusFilterType,
  EVehicleStatusType,
  IAssetPlot,
  IGarminStopPlot,
  IGeofencePlot,
  IHotspotPlot,
  IIndexableObject,
  ILabel,
  IStatusPanelItem,
  IVehiclePlot,
  IWorkOrderPlot
} from '../../models';
import { filterByEntityIds, FilteredEntityIds } from '../../utils';
import { IDeviceHealth, IPlotsState } from '../state';
import { getFilteredEntityIds } from './attribute.selectors';
import { getAllDevicesHealth } from './device-health.selector';

export const getPlotsState = createFeatureSelector<IPlotsState>('plots');

export const getVehiclePlots = createSelector(getPlotsState, (state: IPlotsState): IVehiclePlot[] => state.vehiclePlots);

export const getHotspotPlots = createSelector(getPlotsState, (state: IPlotsState): IHotspotPlot[] => state.hotspotPlots);

export const getGeofencePlots = createSelector(getPlotsState, (state: IPlotsState): IGeofencePlot[] => state.geofencePlots);

export const getGarminStopsPlots = createSelector(getPlotsState, (state: IPlotsState): IGarminStopPlot[] => state.garminStopsPlots);

export const getWorkOrdersPlots = createSelector(getPlotsState, (state: IPlotsState): IWorkOrderPlot[] => state.workOrdersPlots);

export const getAssetPlots = createSelector(getPlotsState, (state: IPlotsState): IAssetPlot[] => state.assetsPlots);

export const getStatusPlotsLoaded = createSelector(
  getPlotsState,
  (state: IPlotsState): boolean => state.assetPlotsLoaded || state.vehiclePlotsLoaded
);

export const getAssetPlotLabels = createSelector(getAssetPlots, (state: IAssetPlot[]): ILabel[] =>
  state.map(
    (assetPlot: IAssetPlot): ILabel => ({
      id: assetPlot.id,
      customElements: [assetPlot.name],
      isMapLabel: false,
      mapEntityType: EPlotType.asset
    })
  )
);

export const getVehiclePlotsTimers = createSelector(
  getPlotsState,
  (state: IPlotsState): IIndexableObject<number> => state.vehiclePlotsTimers
);

export const getStatusPanelPlots = createSelector(
  getVehiclePlots,
  getAssetPlots,
  (vehiclePlots: IVehiclePlot[], assetPlots: IAssetPlot[]): IStatusPanelItem[] => [...vehiclePlots, ...assetPlots] as IStatusPanelItem[]
);

// [Status of the trackable, Statuses to filter it under]
export const statusToFilterableStatusMap = new Map<EStatusFilterType, Set<EStatusFilterType>>([
  [EVehicleStatusType.Moving, new Set([EVehicleStatusType.Moving])],
  [EVehicleStatusType.Stopped, new Set([EVehicleStatusType.Stopped])],
  [EVehicleStatusType.Idle, new Set([EVehicleStatusType.Idle])],
  [EVehicleStatusType.Charging, new Set([EVehicleStatusType.Charging])],
  [EVehicleStatusType.Towing, new Set([EVehicleStatusType.Moving])],
  [EVehicleStatusType.LossOfSignal, new Set([EVehicleStatusType.Stopped])],
  [EVehicleStatusType.Private, new Set([EVehicleStatusType.Stopped])],
  [EVehicleStatusType.Panic, new Set([EVehicleStatusType.Moving, EVehicleStatusType.Idle, EVehicleStatusType.Stopped])]
]);

export const getIsChargeableEVInFleet = createSelector(getStatusPanelPlots, (statusPanelItems: IStatusPanelItem[]): boolean =>
  statusPanelItems.some(item => item.evBatteryData?.batteryLastUpdateTime)
);

export const getStatusFilterCounts = createSelector(
  getStatusPanelPlots,
  getIsChargeableEVInFleet,
  getAllDevicesHealth,
  getFilteredEntityIds,
  (
    statusPanelItems: IStatusPanelItem[],
    isChargeableEVInFleet,
    devicesHealth,
    filteredEntityIds: FilteredEntityIds
  ): Map<EStatusFilterType, number> => {
    const items = filterByEntityIds(statusPanelItems, filteredEntityIds);

    const countMap = new Map<EStatusFilterType, number>([
      [EVehicleStatusType.All, 0],
      [EVehicleStatusType.Moving, 0],
      [EVehicleStatusType.Stopped, 0],
      [EVehicleStatusType.Idle, 0]
    ]);

    // Only add charging entry when there is at least one vehicle that has had a battery update in the past
    if (isChargeableEVInFleet) {
      countMap.set(EVehicleStatusType.Charging, 0);
    }

    items.forEach((statusPanelItem: IStatusPanelItem) => {
      countMap.set(EVehicleStatusType.All, countMap.get(EVehicleStatusType.All) + 1);

      const filterableStatuses = statusToFilterableStatusMap.get(statusPanelItem.status) ?? new Set<EVehicleStatusType>();
      filterableStatuses.forEach(filterableStatus => {
        countMap.set(filterableStatus, countMap.get(filterableStatus) + 1);
      });
    });

    if (devicesHealth) {
      countMap.set(EDeviceErrorType.All, 0);
      countMap.set(EDeviceErrorType.CameraError, 0);
      countMap.set(EDeviceErrorType.TrackerError, 0);

      const devicesWithIssues: IDeviceHealth[] = [];
      items.forEach(statusPanelItem => {
        const devicehealth = devicesHealth.find(
          x => x.vehicleId == statusPanelItem.id.toString() && x.healthStatus.toUpperCase() != 'OPTIMAL'
        );
        if (devicehealth) devicesWithIssues.push(devicehealth);
      });

      if (devicesWithIssues.length > 0) {
        countMap.set(EDeviceErrorType.All, devicesWithIssues.length);
        countMap.set(EDeviceErrorType.CameraError, devicesWithIssues.filter(x => x.cameraHealth.length > 0).length);
        countMap.set(EDeviceErrorType.TrackerError, devicesWithIssues.filter(x => x.componentHealth.length > 0).length);
      }
    }

    return countMap;
  }
);
